/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import "../add-project/addProject.css";
import { UilCheck, UilImageUpload } from "@iconscout/react-unicons";
import { useNavigate, useLocation } from "react-router";
import BulkUpload from "./BulkUpload";
import ProductImageUpload from "./ProductImageUpload";
import PixelColorPicker from "./PixelColorPicker";
import { SizeChartEditor } from "./SizeChartEditor";
import { toast } from "react-toastify";
import axios from "axios";
import { Tooltip } from "react-tooltip";

const SITE_CATEGORY_OPTIONS = [
  { en: "Fit out", ar: "تجهيز" },
  { en: "Furniture", ar: "اثاث" },
  { en: "Upholstery", ar: "تنجيد" },
  { en: "Staircases", ar: "سلالم" },
  { en: "Partitions", ar: "أقسام" },
  { en: "Ceiling", ar: "السقف" },
  { en: "Doors", ar: "أبواب" },
  { en: "Moroccan Handcraft", ar: "صناعة يدوية مغربية" },
];

function flattenCategoryTree(list, depth = 0, out = []) {
  (Array.isArray(list) ? list : []).forEach((c) => {
    out.push({ ...c, __depth: depth });
    if (Array.isArray(c.children) && c.children.length > 0) {
      flattenCategoryTree(c.children, depth + 1, out);
    }
  });
  return out;
}

const AddProduct = () => {
  const [name, setName] = useState("");
  const [arName, setArName] = useState("");
  const [category, setCategory] = useState("");
  const [arCategory, setArCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [price, setPrice] = useState("");
  const [image, setImage] = useState(null);
  const [storeImages, setStoreImages] = useState([]);
  const [storeImagePreviewUrls, setStoreImagePreviewUrls] = useState([]);
  const [enableColors, setEnableColors] = useState(false);
  const [colorMode, setColorMode] = useState("perImage"); // 'perImage' | 'single'
  const [imageColorAssignments, setImageColorAssignments] = useState([]);
  const [colorPickerState, setColorPickerState] = useState({
    open: false,
    imageUrl: "",
    imageIndex: null,
    hexSlot: 0,
  });
  const [alt, setAlt] = useState("");
  const [arAlt, setArAlt] = useState("");
  const [stockQuantity, setStockQuantity] = useState("0");
  const [minDeliveryDays, setMinDeliveryDays] = useState("5");
  const [maxDeliveryDays, setMaxDeliveryDays] = useState("7");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [description, setDescription] = useState("");
  const [arDescription, setArDescription] = useState("");
  const [sizeChart, setSizeChart] = useState(null);
  const [curtainPricing, setCurtainPricing] = useState(null);
  const [curtainSample, setCurtainSample] = useState({
    width: 100,
    height: 150,
    chain: "plastic",
    wAdj: "none",
    hAdj: "none",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();
  const location = useLocation();
  const isStoreContext = location.pathname.startsWith("/dashboard/store");
  const productsEndpoint = isStoreContext ? "/store/products" : "/products";
  const [storeCategories, setStoreCategories] = useState([]);
  const categoryOptions = SITE_CATEGORY_OPTIONS;

  const descEnRef = React.useRef(null);
  const descArRef = React.useRef(null);

  const toggleMarkdownWrap = (ref, setValue, wrapLeft, wrapRight = wrapLeft) => {
    const el = ref?.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setValue((prev) => {
      const s = String(prev || "");
      const selected = s.slice(start, end) || "";
      const hasWrapBeforeSel =
        start >= wrapLeft.length && s.slice(start - wrapLeft.length, start) === wrapLeft;
      const hasWrapAfterSel = s.slice(end, end + wrapRight.length) === wrapRight;
      const canUnwrapSel = selected && hasWrapBeforeSel && hasWrapAfterSel;

      // No selection: allow toggling when caret is between wrappers.
      const noSel = start === end;
      const hasWrapBeforeCaret =
        noSel && start >= wrapLeft.length && s.slice(start - wrapLeft.length, start) === wrapLeft;
      const hasWrapAfterCaret =
        noSel && s.slice(start, start + wrapRight.length) === wrapRight;
      const canUnwrapCaret = noSel && hasWrapBeforeCaret && hasWrapAfterCaret;

      const next = canUnwrapSel
        ? `${s.slice(0, start - wrapLeft.length)}${selected}${s.slice(end + wrapRight.length)}`
        : canUnwrapCaret
        ? `${s.slice(0, start - wrapLeft.length)}${s.slice(start + wrapRight.length)}`
        : `${s.slice(0, start)}${wrapLeft}${selected}${wrapRight}${s.slice(end)}`;

      requestAnimationFrame(() => {
        try {
          el.focus();
          if (canUnwrapSel) {
            el.setSelectionRange(start - wrapLeft.length, end - wrapLeft.length);
          } else if (canUnwrapCaret) {
            el.setSelectionRange(start - wrapLeft.length, start - wrapLeft.length);
          } else if (selected) {
            el.setSelectionRange(start + wrapLeft.length, end + wrapLeft.length);
          } else {
            // No selection: place caret between wrappers.
            el.setSelectionRange(start + wrapLeft.length, start + wrapLeft.length);
          }
        } catch (_) {}
      });
      return next;
    });
  };

  const toggleMarkdownBullets = (ref, setValue) => {
    const el = ref?.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setValue((prev) => {
      const s = String(prev || "");
      const selected = s.slice(start, end) || "";
      const target = selected || s;
      const lines = target.replace(/\r\n/g, "\n").split("\n");
      const nonEmpty = lines.filter((ln) => ln.trim());
      const allBulleted =
        nonEmpty.length > 0 && nonEmpty.every((ln) => ln.trimStart().startsWith("- "));
      const nextBlock = lines
        .map((ln) => {
          if (!ln.trim()) return ln;
          const raw = ln.trimStart();
          if (allBulleted) {
            return raw.startsWith("- ") ? raw.slice(2) : raw;
          }
          return raw.startsWith("- ") ? raw : `- ${raw}`;
        })
        .join("\n");
      const next = selected
        ? `${s.slice(0, start)}${nextBlock}${s.slice(end)}`
        : nextBlock;
      requestAnimationFrame(() => {
        try {
          el.focus();
          if (selected) el.setSelectionRange(start, start + nextBlock.length);
        } catch (_) {}
      });
      return next;
    });
  };

  const selectedStoreCategoryNode = isStoreContext
    ? storeCategories.find((c) => String(c._id) === String(categoryId))
    : null;
  const isCurtainsRootSelected = React.useMemo(() => {
    if (!isStoreContext || !selectedStoreCategoryNode) return false;
    const byId = new Map(storeCategories.map((n) => [String(n._id), n]));
    let cur = selectedStoreCategoryNode;
    let guard = 0;
    while (cur?.parentId && guard < 40) {
      const next = byId.get(String(cur.parentId));
      if (!next) break;
      cur = next;
      guard += 1;
    }
    return String(cur?.slug || "").toLowerCase() === "curtains";
  }, [isStoreContext, selectedStoreCategoryNode, storeCategories]);

  useEffect(() => {
    if (!isCurtainsRootSelected) return;
    setCurtainPricing((prev) =>
      prev || {
        enabled: true,
        basePrice: 0,
        widthAddons: { 100: 0, 125: 0, 150: 0, 175: 0, 200: 0, 225: 0 },
        widthPrices: { 100: 0, 125: 0, 150: 0, 175: 0, 200: 0, 225: 0 },
        heightAddons: { 150: 0, 175: 0, 200: 0, 225: 0, 250: 0, 275: 0 },
        adjustmentAddons: { none: 0, "+5": 0, "+10": 0, "-5": 0, "-10": 0 },
        chainTypeAddons: { metal: 0, plastic: 0 },
      }
    );
  }, [isCurtainsRootSelected]);

  const curtainPricingPreview = React.useMemo(() => {
    if (!isCurtainsRootSelected || !curtainPricing) return null;
    const base = Number(curtainPricing.basePrice ?? 0) || 0;
    const w = String(curtainSample.width);
    const h = String(curtainSample.height);
    const widthAddon = Number(curtainPricing.widthAddons?.[w] ?? 0) || 0;
    const heightAddon = Number(curtainPricing.heightAddons?.[h] ?? 0) || 0;
    const chainAddon = Number(curtainPricing.chainTypeAddons?.[curtainSample.chain] ?? 0) || 0;
    const wAdjAddon = Number(curtainPricing.adjustmentAddons?.[curtainSample.wAdj] ?? 0) || 0;
    const hAdjAddon = Number(curtainPricing.adjustmentAddons?.[curtainSample.hAdj] ?? 0) || 0;
    const total = base + widthAddon + heightAddon + chainAddon + wAdjAddon + hAdjAddon;
    return { base, widthAddon, heightAddon, chainAddon, wAdjAddon, hAdjAddon, total };
  }, [isCurtainsRootSelected, curtainPricing, curtainSample]);

  useEffect(() => {
    if (!isStoreContext) return;
    axios
      .get("/store/categories/tree?includeInactive=1")
      .then(({ data }) => {
        setStoreCategories(flattenCategoryTree(data || []));
      })
      .catch(() => {
        setStoreCategories([]);
      });
  }, [isStoreContext]);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!isStoreContext) return;
    const nextAssignments = storeImages.map((_, index) => {
      const prev = imageColorAssignments[index] || {};
      return {
        name: prev.name || "",
        hexes: Array.isArray(prev.hexes) && prev.hexes.length > 0 ? prev.hexes : [prev.hex || ""].filter(Boolean),
        imageIndex: index,
      };
    });
    setImageColorAssignments(nextAssignments);
    if (storeImages.length === 0) {
      setEnableColors(false);
      setColorMode("perImage");
      setColorPickerState({ open: false, imageUrl: "", imageIndex: null, hexSlot: 0 });
    }
  }, [storeImages, isStoreContext]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isStoreContext) return;
    const revokableUrls = [];
    const nextUrls = storeImages.map((img) => {
      if (img instanceof File) {
        const objectUrl = URL.createObjectURL(img);
        revokableUrls.push(objectUrl);
        return objectUrl;
      }
      return img?.url || "";
    });
    setStoreImagePreviewUrls(nextUrls);
    return () => {
      revokableUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [storeImages, isStoreContext]);

  const handleFileUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setError("");
    } else {
      setError("Please upload a valid image file (png, jpg, jpeg).");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleRemoveImage = () => {
    setImage(null);
  };

  const handleColorAssignmentChange = (index, field, value) => {
    setImageColorAssignments((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              [field]: value,
              imageIndex: i,
            }
          : entry
      )
    );
  };

  const handleColorHexChange = (index, slot, value) => {
    setImageColorAssignments((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const nextHexes = Array.isArray(entry.hexes) ? [...entry.hexes] : [];
        while (nextHexes.length <= slot) nextHexes.push("");
        nextHexes[slot] = value;
        return { ...entry, hexes: nextHexes, imageIndex: i };
      })
    );
  };

  const openColorPicker = (index, hexSlot = 0) => {
    const imageUrl = storeImagePreviewUrls[index];
    if (!imageUrl) return;
    setColorPickerState({
      open: true,
      imageUrl,
      imageIndex: index,
      hexSlot,
    });
  };

  const MAX_IMAGE_COLORS = 4;
  const addColorSlot = (index) => {
    setImageColorAssignments((prev) =>
      (Array.isArray(prev) ? prev : []).map((entry, i) => {
        if (i !== index) return entry;
        const nextHexes = Array.isArray(entry.hexes) ? [...entry.hexes] : [];
        if (nextHexes.length >= MAX_IMAGE_COLORS) return entry;
        nextHexes.push("");
        return { ...entry, hexes: nextHexes, imageIndex: i };
      })
    );
  };
  const removeColorSlot = (index) => {
    setImageColorAssignments((prev) =>
      (Array.isArray(prev) ? prev : []).map((entry, i) => {
        if (i !== index) return entry;
        const nextHexes = Array.isArray(entry.hexes) ? [...entry.hexes] : [];
        if (nextHexes.length <= 1) return entry;
        nextHexes.pop();
        return { ...entry, hexes: nextHexes, imageIndex: i };
      })
    );
  };

  const imageFormats = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tiff",
    "webp",
    "svg",
    "heic",
    "ico",
    "avif",
  ];

  const handleAddProduct = async (e) => {
    e.preventDefault();

    const missingRequired =
      !name ||
      !arName ||
      !category ||
      !arCategory ||
      (isStoreContext ? !String(categoryId || "").trim() : false) ||
      (isStoreContext ? storeImages.length === 0 : !image);

    if (missingRequired) {
      return toast.error("Please fill out all fields.");
    }

    let discountPct = 0;
    if (isStoreContext) {
      const raw = String(discountPercentage ?? "").trim();
      if (raw !== "") {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          return toast.error("Discount must be between 0 and 100.");
        }
        discountPct = Math.round(n);
      }
    }

    const productData = new FormData();
    productData.append("name", name);
    productData.append("arName", arName);
    productData.append("category", category);
    productData.append("arCategory", arCategory);
    productData.append("price", price || "0");
    const resolvedAlt = String(alt || "").trim() || String(name || "").trim();
    const resolvedArAlt =
      String(arAlt || "").trim() || String(arName || "").trim() || String(name || "").trim();
    productData.append("alt", resolvedAlt);
    productData.append("arAlt", resolvedArAlt);
    if (isStoreContext) {
      productData.append("discount_percentage", String(discountPct));
      productData.append("stock_quantity", String(stockQuantity ?? "0"));
      productData.append("min_delivery_days", String(minDeliveryDays ?? "5"));
      productData.append("max_delivery_days", String(maxDeliveryDays ?? "7"));
      productData.append("description", description);
      productData.append("arDescription", arDescription);
      if (String(categoryId || "").trim()) productData.append("categoryId", String(categoryId).trim());
      if (sizeChart) productData.append("sizeChart", JSON.stringify(sizeChart));
      if (curtainPricing) productData.append("curtainPricing", JSON.stringify(curtainPricing));
      storeImages.forEach((img) => {
        if (img instanceof File) {
          productData.append("images", img);
        }
      });
      const colorsPayload = enableColors
        ? (colorMode === "single"
            ? [
                {
                  name: (imageColorAssignments?.[0]?.name || "").trim() || "Color",
                  hexes: (Array.isArray(imageColorAssignments?.[0]?.hexes) ? imageColorAssignments[0].hexes : [])
                    .map((h) => String(h || "").trim())
                    .filter(Boolean)
                    .slice(0, 4),
                  hex: String(
                    (Array.isArray(imageColorAssignments?.[0]?.hexes) ? imageColorAssignments[0].hexes[0] : "") || ""
                  ).trim(),
                  imageIndex: 0,
                },
              ].filter((entry) => entry.name || entry.hex || (entry.hexes && entry.hexes.length > 0))
            : imageColorAssignments
                .map((entry, index) => ({
                  name: (entry.name || "").trim() || `Color ${index + 1}`,
                  hexes: (Array.isArray(entry.hexes) ? entry.hexes : [])
                    .map((h) => String(h || "").trim())
                    .filter(Boolean)
                    .slice(0, 4),
                  hex: String((Array.isArray(entry.hexes) ? entry.hexes[0] : "") || "").trim(),
                  imageIndex: index,
                }))
                .filter((entry) => entry.name || entry.hex || (entry.hexes && entry.hexes.length > 0)))
        : [];
      productData.append("colors", JSON.stringify(colorsPayload));
      productData.append("colorMode", colorMode);
    } else {
      productData.append("image", image);
    }

    try {
      setLoading(true);
      await toast.promise(
        axios.post(productsEndpoint, productData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }),
        {
          pending: "Adding product...",
          success: "Product added successfully!",
        }
      );

      setName("");
      setArName("");
      setCategory("");
      setArCategory("");
      setPrice("");
      setImage(null);
      setStoreImages([]);
      setStoreImagePreviewUrls([]);
      setEnableColors(false);
      setColorMode("perImage");
      setImageColorAssignments([]);
      setColorPickerState({ open: false, imageUrl: "", imageIndex: null, hexSlot: 0 });
      setAlt("");
      setArAlt("");
      if (isStoreContext) {
        setDiscountPercentage("");
        setDescription("");
        setArDescription("");
        setSizeChart(null);
        setCategoryId("");
        navigate("/dashboard/store/products");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className="addProject">
        <form className="container" onSubmit={handleAddProduct}>
          <div className="title">
            <div className="text">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="35"
                viewBox="0 0 34 35"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.3511 2.271C9.21636 2.271 8.43861 2.271 7.74444 2.45941C7.02601 2.65659 6.35707 3.00266 5.78108 3.47517C5.20509 3.94768 4.73492 4.53605 4.40111 5.20208C4.07952 5.84241 3.92794 6.606 3.70552 7.7195L2.82719 12.1083C2.67164 12.8777 2.68667 13.6718 2.87122 14.4347C3.05577 15.1977 3.40536 15.9109 3.89536 16.5241V20.4128C3.89536 23.0167 3.89536 25.0793 4.11211 26.6929C4.33594 28.3532 4.80627 29.6977 5.86594 30.7587C6.92561 31.8184 8.27003 32.2887 9.93178 32.5126C11.5454 32.7293 13.608 32.7293 16.2104 32.7293H17.7872C20.391 32.7293 22.4537 32.7293 24.0673 32.5126C25.7276 32.2887 27.072 31.8184 28.1331 30.7587C29.1928 29.6977 29.6631 28.3532 29.8869 26.6929C30.1037 25.0779 30.1037 23.0167 30.1037 20.4128V16.5255C30.5937 15.912 30.9431 15.1985 31.1274 14.4353C31.3117 13.6721 31.3264 12.8778 31.1704 12.1083L30.2921 7.7195C30.0711 6.606 29.9181 5.84241 29.5979 5.20208C29.264 4.5359 28.7936 3.94744 28.2173 3.47492C27.6411 3.00241 26.9719 2.65641 26.2532 2.45941C25.5604 2.271 24.7827 2.271 23.6465 2.271H10.3511ZM25.8792 18.5627C26.6002 18.564 27.3142 18.4204 27.9787 18.1405V20.3335C27.9787 23.0351 27.9759 24.9547 27.7804 26.411C27.5891 27.8347 27.2293 28.6564 26.63 29.2557C26.0308 29.8549 25.2091 30.2147 23.7825 30.406C23.0593 30.4948 22.3321 30.5487 21.6037 30.5675V26.6632C21.6037 26.0398 21.6037 25.5015 21.564 25.0623C21.5215 24.5948 21.428 24.133 21.1759 23.6981C20.8964 23.2134 20.4943 22.8108 20.0099 22.5307C19.575 22.28 19.1132 22.1865 18.6457 22.144C18.2065 22.1043 17.6682 22.1043 17.0449 22.1043H16.9528C16.3294 22.1043 15.7925 22.1043 15.3519 22.144C14.8844 22.1865 14.424 22.28 13.9891 22.5307C13.5042 22.8106 13.1016 23.2132 12.8218 23.6981C12.571 24.133 12.4775 24.5948 12.435 25.0609C12.3954 25.5029 12.3954 26.0398 12.3954 26.6617V30.5675C11.666 30.5488 10.9379 30.4949 10.2137 30.406C8.78994 30.2147 7.96828 29.8549 7.36903 29.2557C6.76977 28.6564 6.40994 27.8347 6.21869 26.4096C6.02177 24.9547 6.02036 23.0351 6.02036 20.3335V18.1405C7.15254 18.618 8.41376 18.6949 9.59557 18.3585C10.7774 18.0221 11.809 17.2925 12.52 16.2903C13.034 16.9961 13.708 17.57 14.4866 17.965C15.2653 18.36 16.1264 18.5648 16.9995 18.5627C17.8726 18.5648 18.7338 18.36 19.5124 17.965C20.2911 17.57 20.965 16.9961 21.479 16.2903C21.977 16.9936 22.6366 17.5671 23.4023 17.9625C24.168 18.3579 25.0174 18.5637 25.8792 18.5627ZM14.5204 30.6015C15.07 30.6034 15.6603 30.6043 16.2912 30.6043H17.7079C18.3387 30.6043 18.929 30.6034 19.4787 30.6015V26.7085C19.4787 26.0271 19.4773 25.5865 19.4475 25.2536C19.4192 24.9348 19.3696 24.8187 19.3356 24.7606C19.2425 24.5994 19.1086 24.4656 18.9474 24.3724C18.8894 24.3384 18.7732 24.2902 18.4544 24.2605C17.9701 24.2309 17.4847 24.2205 16.9995 24.2293C16.3181 24.2293 15.8775 24.2293 15.5446 24.2605C15.2244 24.2888 15.1097 24.3384 15.0516 24.3724C14.8899 24.4654 14.7556 24.5992 14.662 24.7606C14.6294 24.8172 14.5799 24.9348 14.5515 25.2536C14.5218 25.5865 14.5204 26.0271 14.5204 26.7085V30.6015ZM12.282 4.396H10.5084C9.15686 4.396 8.68794 4.40591 8.30827 4.50933C7.87729 4.62754 7.47598 4.83505 7.13039 5.11841C6.7848 5.40176 6.50266 5.75462 6.30227 6.15408C6.12519 6.50683 6.02461 6.963 5.75969 8.289L4.91252 12.5248C4.82224 12.9607 4.8218 13.4103 4.91125 13.8463C5.00069 14.2823 5.17814 14.6955 5.43273 15.0606C5.68732 15.4257 6.01371 15.735 6.39193 15.9696C6.77016 16.2042 7.19226 16.3592 7.63244 16.4251C8.07262 16.491 8.52161 16.4664 8.95197 16.3529C9.38232 16.2393 9.78498 16.0391 10.1353 15.7646C10.4857 15.4901 10.7763 15.147 10.9896 14.7563C11.2028 14.3657 11.334 13.9356 11.3754 13.4924L11.4731 12.5206L11.4788 12.4611L12.282 4.396ZM13.5868 12.7189L14.4198 4.396H19.5793L20.4066 12.6722C20.453 13.1457 20.4001 13.6237 20.2515 14.0757C20.1028 14.5277 19.8615 14.9438 19.5431 15.2973C19.2247 15.6509 18.836 15.9342 18.402 16.1292C17.968 16.3242 17.4981 16.4265 17.0223 16.4298C16.5465 16.4331 16.0752 16.3371 15.6386 16.1481C15.2019 15.9591 14.8094 15.6811 14.4862 15.332C14.1629 14.9828 13.916 14.5701 13.7612 14.1202C13.6063 13.6703 13.5469 13.1931 13.5868 12.7189ZM25.6908 4.50933C25.3111 4.40591 24.8422 4.396 23.4907 4.396H21.7142L22.6251 13.491C22.6678 13.9329 22.7999 14.3615 23.0135 14.7506C23.2271 15.1398 23.5177 15.4814 23.8676 15.7547C24.2174 16.028 24.6193 16.2271 25.0486 16.3401C25.4779 16.4531 25.9257 16.4775 26.3648 16.4119C26.8039 16.3462 27.225 16.1919 27.6025 15.9582C27.98 15.7246 28.306 15.4166 28.5606 15.053C28.8152 14.6893 28.9932 14.2776 29.0836 13.843C29.174 13.4084 29.175 12.9599 29.0865 12.5248L28.2408 8.289C27.9744 6.963 27.8739 6.50683 27.6982 6.15408C27.4977 5.75447 27.2153 5.40152 26.8695 5.11816C26.5236 4.8348 26.122 4.62736 25.6908 4.50933Z"
                  fill="black"
                  fill-opacity="0.6"
                />
              </svg>
              <p>Add New Product</p>
            </div>
            <button>
              <UilCheck size={22} />
              Add Product
            </button>
          </div>
          <div className="form">
            <div className="inputs">
              <h3>General information</h3>
              <div className="input">
                <label>
                  Name <span>*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  placeholder="Give your product a short and clear name."
                />
              </div>
              <div className="input ar-input">
                <label>
                  الاسم <span>*</span>
                </label>
                <input
                  value={arName}
                  onChange={(e) => setArName(e.target.value)}
                  type="text"
                  placeholder="أعط منتجك اسمًا قصيرًا وواضحًا."
                />
              </div>
              <hr />
              <div className="input">
                <label>
                  Category <span>*</span>
                </label>
                {isStoreContext ? (
                  <select
                    value={categoryId}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setCategoryId(nextId);
                      const picked = storeCategories.find((c) => String(c._id) === String(nextId));
                      if (picked) {
                        setCategory(picked.label || "");
                        setArCategory(picked.arLabel || picked.label || "");
                      } else {
                        setCategory("");
                        setArCategory("");
                      }
                    }}
                  >
                    <option value="" hidden>
                      Choose category
                    </option>
                    {storeCategories.map((c) => {
                      const indent = "—".repeat(Math.min(6, c.__depth || 0));
                      const en = c.label || "";
                      const ar = c.arLabel || "";
                      return (
                        <option key={c._id} value={c._id}>
                          {indent} {en} {ar ? `— ${ar}` : ""}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="" hidden>
                      Choose your product category.
                    </option>
                    <option value="" disabled>
                      Categories
                    </option>
                    {categoryOptions.map((option) => (
                      <option key={option.en} value={option.en}>
                        {option.en}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {!isStoreContext ? (
                <div className="input ar-input">
                  <label>
                    الفئة <span>*</span>
                  </label>
                  <select value={arCategory} onChange={(e) => setArCategory(e.target.value)}>
                    <option value="" hidden>
                      اختر فئة المنتج الخاص بك.
                    </option>
                    <option value="" disabled>
                      الفئات
                    </option>
                    {categoryOptions.map((option) => (
                      <option key={option.ar} value={option.ar}>
                        {option.ar}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="input">
                <label>Price (SAR)</label>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Enter product price"
                />
              </div>
              {isStoreContext ? (
                <>
                  <div className="input">
                    <label>Stock quantity</label>
                    <input
                      value={stockQuantity}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                    />
                  </div>
                  <div className="input">
                    <label>Min delivery (days)</label>
                    <input
                      value={minDeliveryDays}
                      onChange={(e) => setMinDeliveryDays(e.target.value)}
                      type="number"
                      min="0"
                      step="1"
                      placeholder="5"
                    />
                  </div>
                  <div className="input">
                    <label>Max delivery (days)</label>
                    <input
                      value={maxDeliveryDays}
                      onChange={(e) => setMaxDeliveryDays(e.target.value)}
                      type="number"
                      min="0"
                      step="1"
                      placeholder="7"
                    />
                  </div>
                  <div className="input">
                    <label>Discount (%)</label>
                    <input
                      value={discountPercentage}
                      onChange={(e) => setDiscountPercentage(e.target.value)}
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      placeholder="0 (optional)"
                    />
                  </div>
                  <div className="input">
                    <label>Product description (English)</label>
                    <div
                      className="store-desc-tools"
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <button
                        type="button"
                        title="Bold"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,0.12)",
                          background: "#fff",
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleMarkdownWrap(descEnRef, setDescription, "**")}
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        title="Italic"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,0.12)",
                          background: "#fff",
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleMarkdownWrap(descEnRef, setDescription, "_")}
                      >
                        <em>I</em>
                      </button>
                      <button
                        type="button"
                        title="Bullets"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,0.12)",
                          background: "#fff",
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleMarkdownBullets(descEnRef, setDescription)}
                      >
                        •
                      </button>
                    </div>
                    <textarea
                      ref={descEnRef}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={5}
                      placeholder="Details, materials, dimensions — shown on the store product page and quick view."
                    />
                  </div>
                  <div className="input ar-input">
                    <label>وصف المنتج (عربي)</label>
                    <div
                      className="store-desc-tools"
                      style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <button
                        type="button"
                        title="Bold"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,0.12)",
                          background: "#fff",
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleMarkdownWrap(descArRef, setArDescription, "**")}
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        title="Italic"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,0.12)",
                          background: "#fff",
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleMarkdownWrap(descArRef, setArDescription, "_")}
                      >
                        <em>I</em>
                      </button>
                      <button
                        type="button"
                        title="Points"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: "1px solid rgba(0,0,0,0.12)",
                          background: "#fff",
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                        onClick={() => toggleMarkdownBullets(descArRef, setArDescription)}
                      >
                        •
                      </button>
                    </div>
                    <textarea
                      ref={descArRef}
                      value={arDescription}
                      onChange={(e) => setArDescription(e.target.value)}
                      rows={5}
                      placeholder="الوصف المعروض في المتجر عند اختيار العربية."
                      dir="rtl"
                    />
                  </div>
                  <SizeChartEditor value={sizeChart} onChange={setSizeChart} />
                  {isCurtainsRootSelected ? (
                    <div className="input" style={{ marginTop: 12 }}>
                      <label>Curtains pricing (base + add-ons)</label>
                      <p style={{ margin: "6px 0 10px", opacity: 0.75 }}>
                        Set the minimum base price (100×150 + Plastic), then add-ons for other sizes/options. Discount (if set) applies to the total.
                      </p>
                      <div style={{ display: "grid", gap: 10 }}>
                        <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={curtainPricing?.enabled !== false}
                            onChange={(e) =>
                              setCurtainPricing((prev) => ({ ...(prev || {}), enabled: e.target.checked }))
                            }
                          />
                          Enable curtains dynamic pricing
                        </label>

                        <div style={{ display: "grid", gap: 8 }}>
                          <strong>Base minimum price (SAR)</strong>
                          <label style={{ display: "grid", gap: 4, maxWidth: 260 }}>
                            <span>100 cm × 150 cm + Plastic</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={curtainPricing?.basePrice ?? 0}
                              onChange={(e) =>
                                setCurtainPricing((prev) => ({
                                  ...(prev || {}),
                                  basePrice: Number(e.target.value),
                                }))
                              }
                            />
                          </label>
                        </div>

                        <div style={{ display: "grid", gap: 8 }}>
                          <strong>Width add-ons (SAR)</strong>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                            {[100, 125, 150, 175, 200, 225].map((w) => (
                              <label key={w} style={{ display: "grid", gap: 4 }}>
                                <span>{w} cm</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={curtainPricing?.widthAddons?.[w] ?? 0}
                                  disabled={w === 100}
                                  onChange={(e) =>
                                    setCurtainPricing((prev) => ({
                                      ...(prev || {}),
                                      widthAddons: { ...(prev?.widthAddons || {}), [w]: Number(e.target.value) },
                                    }))
                                  }
                                />
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: "grid", gap: 8 }}>
                          <strong>Height add-ons (SAR)</strong>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                            {[150, 175, 200, 225, 250, 275].map((h) => (
                              <label key={h} style={{ display: "grid", gap: 4 }}>
                                <span>{h} cm</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={curtainPricing?.heightAddons?.[h] ?? 0}
                                  disabled={h === 150}
                                  onChange={(e) =>
                                    setCurtainPricing((prev) => ({
                                      ...(prev || {}),
                                      heightAddons: { ...(prev?.heightAddons || {}), [h]: Number(e.target.value) },
                                    }))
                                  }
                                />
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: "grid", gap: 8 }}>
                          <strong>Width/height adjustment add-ons (SAR)</strong>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                            {[
                              ["none", "No adjustment"],
                              ["+5", "Add +5 cm"],
                              ["+10", "Add +10 cm"],
                              ["-5", "Remove -5 cm"],
                              ["-10", "Remove -10 cm"],
                            ].map(([k, label]) => (
                              <label key={k} style={{ display: "grid", gap: 4 }}>
                                <span>{label}</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={curtainPricing?.adjustmentAddons?.[k] ?? 0}
                                  disabled={k === "none"}
                                  onChange={(e) =>
                                    setCurtainPricing((prev) => ({
                                      ...(prev || {}),
                                      adjustmentAddons: { ...(prev?.adjustmentAddons || {}), [k]: Number(e.target.value) },
                                    }))
                                  }
                                />
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: "grid", gap: 8 }}>
                          <strong>Chain type add-ons (SAR)</strong>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                            {[
                              ["metal", "Metal"],
                              ["plastic", "Plastic"],
                            ].map(([k, label]) => (
                              <label key={k} style={{ display: "grid", gap: 4 }}>
                                <span>{label}</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={curtainPricing?.chainTypeAddons?.[k] ?? 0}
                                  disabled={k === "plastic"}
                                  onChange={(e) =>
                                    setCurtainPricing((prev) => ({
                                      ...(prev || {}),
                                      chainTypeAddons: { ...(prev?.chainTypeAddons || {}), [k]: Number(e.target.value) },
                                    }))
                                  }
                                />
                              </label>
                            ))}
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: 6,
                            padding: 12,
                            borderRadius: 14,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "rgba(255,255,255,0.75)",
                            display: "grid",
                            gap: 10,
                          }}
                        >
                          <strong>Live pricing preview</strong>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                            <label style={{ display: "grid", gap: 4 }}>
                              <span>Sample width</span>
                              <select
                                value={curtainSample.width}
                                onChange={(e) => setCurtainSample((p) => ({ ...p, width: Number(e.target.value) }))}
                              >
                                {[100, 125, 150, 175, 200, 225].map((n) => (
                                  <option key={n} value={n}>
                                    {n} cm
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label style={{ display: "grid", gap: 4 }}>
                              <span>Sample height</span>
                              <select
                                value={curtainSample.height}
                                onChange={(e) => setCurtainSample((p) => ({ ...p, height: Number(e.target.value) }))}
                              >
                                {[150, 175, 200, 225, 250, 275].map((n) => (
                                  <option key={n} value={n}>
                                    {n} cm
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label style={{ display: "grid", gap: 4 }}>
                              <span>Sample chain</span>
                              <select
                                value={curtainSample.chain}
                                onChange={(e) => setCurtainSample((p) => ({ ...p, chain: e.target.value }))}
                              >
                                <option value="plastic">Plastic</option>
                                <option value="metal">Metal</option>
                              </select>
                            </label>
                            <label style={{ display: "grid", gap: 4 }}>
                              <span>Width adjustment</span>
                              <select
                                value={curtainSample.wAdj}
                                onChange={(e) => setCurtainSample((p) => ({ ...p, wAdj: e.target.value }))}
                              >
                                <option value="none">No adjustment</option>
                                <option value="+5">Add +5 cm</option>
                                <option value="+10">Add +10 cm</option>
                                <option value="-5">Remove -5 cm</option>
                                <option value="-10">Remove -10 cm</option>
                              </select>
                            </label>
                            <label style={{ display: "grid", gap: 4 }}>
                              <span>Height adjustment</span>
                              <select
                                value={curtainSample.hAdj}
                                onChange={(e) => setCurtainSample((p) => ({ ...p, hAdj: e.target.value }))}
                              >
                                <option value="none">No adjustment</option>
                                <option value="+5">Add +5 cm</option>
                                <option value="+10">Add +10 cm</option>
                                <option value="-5">Remove -5 cm</option>
                                <option value="-10">Remove -10 cm</option>
                              </select>
                            </label>
                          </div>

                          {curtainPricingPreview ? (
                            <div
                              style={{
                                padding: "10px 12px",
                                borderRadius: 12,
                                background: "rgba(154,116,98,0.10)",
                                border: "1px solid rgba(154,116,98,0.18)",
                                fontWeight: 900,
                              }}
                            >
                              Base {curtainPricingPreview.base} + Width {curtainPricingPreview.widthAddon} + Height{" "}
                              {curtainPricingPreview.heightAddon} + Chain {curtainPricingPreview.chainAddon} + Adjustments{" "}
                              {curtainPricingPreview.wAdjAddon + curtainPricingPreview.hAdjAddon} ={" "}
                              {curtainPricingPreview.total} SAR
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="image">
              <h3>{isStoreContext ? "Upload images" : "Upload image"}</h3>

              {isStoreContext ? (
                <ProductImageUpload
                  images={storeImages}
                  onChange={(nextImages) => {
                    setStoreImages(nextImages);
                    if (nextImages.length > 0) setError("");
                  }}
                  error={error}
                  disabled={loading}
                />
              ) : image ? (
                <div className="imgPreview">
                  <img src={URL.createObjectURL(image)} alt="Uploaded" />
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={handleRemoveImage}
                  >
                    <i className="uil uil-times"></i>
                  </button>
                </div>
              ) : (
                <div
                  className="imgInput"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <UilImageUpload size={40} />
                  <p>Drop your main image here, or</p>
                  <div className="btn">
                    <button type="button">Click here</button>
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      accept="image/*"
                    />
                  </div>
                </div>
              )}

              {!isStoreContext && error && <p className="error">{error}</p>}

              {!isStoreContext && !image && (
                <>
                  <div className="image-p">
                    <p className="inf" id="inf">
                      Upload your product image.
                    </p>
                    <i className="uil uil-info-circle" id="formats"></i>
                  </div>
                  <Tooltip
                    anchorSelect="#formats"
                    content={imageFormats.join(", ")}
                    place="top"
                  />
                </>
              )}

              <div className="input">
                <label>Image alt (optional)</label>
                <input
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  type="text"
                  placeholder="Give your product image a short alt text."
                />
              </div>
              <div className="input ar-input">
                <label>نص الصورة البديل (اختياري)</label>
                <input
                  value={arAlt}
                  onChange={(e) => setArAlt(e.target.value)}
                  type="text"
                  placeholder="أعط صورة مشروعك نصًا بديلًا قصيرًا."
                />
              </div>
              {isStoreContext && storeImages.length > 0 && (
                <div className="store-colors-setup">
                  <label className="store-colors-toggle">
                    <input
                      type="checkbox"
                      checked={enableColors}
                      onChange={(e) => setEnableColors(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Enable Colors for this product</span>
                  </label>

                  {enableColors && (
                    <div className="store-colors-list">
                      <label className="store-colors-toggle" style={{ marginBottom: 10 }}>
                        <input
                          type="checkbox"
                          checked={colorMode === "single"}
                          onChange={(e) => setColorMode(e.target.checked ? "single" : "perImage")}
                          disabled={loading}
                        />
                        <span>Same color for all images</span>
                      </label>

                      {(colorMode === "single" ? [storeImagePreviewUrls[0]] : storeImagePreviewUrls).map((previewUrl, index) => (
                        <div key={index} className="store-color-card">
                          <p className="store-color-card-title">
                            {colorMode === "single" ? "All images" : `Image ${index + 1}`}
                          </p>
                          <div className="store-color-card-preview">
                            <img src={previewUrl} alt="" />
                          </div>
                          <div className="input">
                            <label>Color Name</label>
                            <input
                              type="text"
                              value={(colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.name || ""}
                              onChange={(e) =>
                                handleColorAssignmentChange(colorMode === "single" ? 0 : index, "name", e.target.value)
                              }
                              placeholder={colorMode === "single" ? "e.g. Color" : `e.g. Color ${index + 1}`}
                              disabled={loading}
                            />
                          </div>
                          <div className="store-color-code-row">
                            <div className="input">
                              <label>Color 1 (Hex)</label>
                              <input
                                type="text"
                                value={(colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes?.[0] || ""}
                                onChange={(e) =>
                                  handleColorHexChange(colorMode === "single" ? 0 : index, 0, e.target.value)
                                }
                                placeholder="#000000"
                                disabled={loading}
                              />
                            </div>
                            <input
                              type="color"
                              value={
                                /^#([0-9a-f]{6})$/i.test(
                                  (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes?.[0] || ""
                                )
                                  ? (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index]).hexes[0]
                                  : "#000000"
                              }
                              onChange={(e) =>
                                handleColorHexChange(colorMode === "single" ? 0 : index, 0, e.target.value)
                              }
                              className="store-color-native-picker"
                              aria-label={`Select color for image ${index + 1}`}
                              disabled={loading}
                            />
                          </div>
                          {(((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes) || []).slice(1).map((_, slotIdx) => {
                            const slot = slotIdx + 1;
                            const current = (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes?.[slot] || "";
                            const fallback = slot === 1 ? "#ffffff" : "#000000";
                            return (
                              <div key={`${index}-slot-${slot}`} className="store-color-code-row">
                                <div className="input">
                                  <label>{`Color ${slot + 1} (Hex)`}</label>
                                  <input
                                    type="text"
                                    value={current}
                                    onChange={(e) => handleColorHexChange(colorMode === "single" ? 0 : index, slot, e.target.value)}
                                    placeholder="#FFFFFF (optional)"
                                    disabled={loading}
                                  />
                                </div>
                                <input
                                  type="color"
                                  value={/^#([0-9a-f]{6})$/i.test(current) ? current : fallback}
                                  onChange={(e) => handleColorHexChange(colorMode === "single" ? 0 : index, slot, e.target.value)}
                                  className="store-color-native-picker"
                                  aria-label={`Select color ${slot + 1} for image ${index + 1}`}
                                  disabled={loading}
                                />
                              </div>
                            );
                          })}

                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                            <button
                              type="button"
                              className="store-color-extract-btn"
                              onClick={() => openColorPicker(colorMode === "single" ? 0 : index, 0)}
                              disabled={loading}
                            >
                              Extract color 1 from image
                            </button>
                            {(() => {
                              const target = colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index];
                              const slots = Array.isArray(target?.hexes) ? target.hexes.length : 0;
                              return slots >= 2 ? (
                                <button
                                  type="button"
                                  className="store-color-extract-btn"
                                  onClick={() => openColorPicker(colorMode === "single" ? 0 : index, 1)}
                                  disabled={loading}
                                >
                                  Extract color 2 from image
                                </button>
                              ) : null;
                            })()}
                            <button
                              type="button"
                              className="store-color-extract-btn"
                              onClick={() => addColorSlot(colorMode === "single" ? 0 : index)}
                              disabled={
                                loading ||
                                (Array.isArray((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes)
                                  ? (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index]).hexes.length >= MAX_IMAGE_COLORS
                                  : true)
                              }
                            >
                              Add color
                            </button>
                            <button
                              type="button"
                              className="store-color-extract-btn"
                              onClick={() => removeColorSlot(colorMode === "single" ? 0 : index)}
                              disabled={
                                loading ||
                                !Array.isArray((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes) ||
                                (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index]).hexes.length <= 1
                              }
                            >
                              Remove color
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </form>

        {isStoreContext && (
          <BulkUpload
            onSuccess={() => {
              navigate("/dashboard/store/products");
            }}
          />
        )}
      </main>
      {colorPickerState.open && (
        <PixelColorPicker
          imageUrl={colorPickerState.imageUrl}
          onPickColor={(hex) => {
            if (typeof colorPickerState.imageIndex === "number") {
              handleColorHexChange(colorPickerState.imageIndex, colorPickerState.hexSlot || 0, hex);
            }
            setColorPickerState({ open: false, imageUrl: "", imageIndex: null, hexSlot: 0 });
          }}
          onClose={() =>
            setColorPickerState({ open: false, imageUrl: "", imageIndex: null, hexSlot: 0 })
          }
        />
      )}
    </>
  );
};

export default AddProduct;
